import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  getDeviceQueryKey,
  getDevicesQueryKey,
  getUserQueryKey,
} from "src/config/request-keys";
import { captureSentryException } from "src/utils/sentry";
import {
  isDeviceSystemDataKey,
  isOrganizationSystemDataKey,
} from "src/utils/system-data-keys";
import { invalidateAnswerQueries } from "./invalidateAnswerQueries";
import { resolveEditDataKeySource } from "./messages/tools/editDataKeySource";
import { DATA_KEY_EDIT_TOOL_NAMES } from "./messages/tools/editDataKeyValue";
import { getToolDeviceId } from "./messages/tools/toolDeviceId";
import { getChatMessageType, type ChatMessage } from "./types";

const getToolCallsById = (messages: ChatMessage[]) => {
  const toolCallsById = new Map<
    string,
    { name: string; deviceId: string; documentId?: string; dataKeyId?: string }
  >();

  messages.forEach((message) => {
    if (getChatMessageType(message) !== "ai") {
      return;
    }

    message.tool_calls?.forEach((toolCall) => {
      if (
        typeof toolCall.id === "string" &&
        typeof toolCall.name === "string"
      ) {
        const source = resolveEditDataKeySource(toolCall.args ?? {});
        const args = toolCall.args;
        toolCallsById.set(toolCall.id, {
          name: toolCall.name,
          dataKeyId:
            typeof args === "object" &&
            args !== null &&
            "id" in args &&
            typeof args.id === "string"
              ? args.id
              : undefined,
          deviceId: getToolDeviceId(toolCall.args),
          documentId:
            source.kind === "document" ? source.documentId : undefined,
        });
      }
    });
  });

  return toolCallsById;
};

export const useInvalidateAnswersOnDataKeyApply = ({
  messages,
  orgId,
}: {
  messages: ChatMessage[] | undefined;
  orgId: string;
}) => {
  const queryClient = useQueryClient();
  const processedToolCallIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!messages?.length) {
      return;
    }

    const toolCallsById = getToolCallsById(messages);
    const newlyProcessedToolCallIds = new Set<string>();
    const targetDeviceIds = new Set<string>();
    const targetDocumentIds = new Set<string>();
    const systemDeviceIds = new Set<string>();
    let organizationChanged = false;

    messages.forEach((message) => {
      if (
        getChatMessageType(message) !== "tool" ||
        message.status !== "success"
      ) {
        return;
      }

      const toolCallId = message.tool_call_id;
      if (
        typeof toolCallId !== "string" ||
        processedToolCallIdsRef.current.has(toolCallId)
      ) {
        return;
      }

      const toolCallName =
        typeof message.name === "string" && message.name.length > 0
          ? message.name
          : toolCallsById.get(toolCallId)?.name;

      if (!toolCallName || !DATA_KEY_EDIT_TOOL_NAMES.has(toolCallName)) {
        return;
      }

      newlyProcessedToolCallIds.add(toolCallId);
      const target = toolCallsById.get(toolCallId);
      if (isOrganizationSystemDataKey(target?.dataKeyId))
        organizationChanged = true;
      if (isDeviceSystemDataKey(target?.dataKeyId) && target?.deviceId)
        systemDeviceIds.add(target.deviceId);
      const targetDocumentId = toolCallsById.get(toolCallId)?.documentId;
      if (targetDocumentId) targetDocumentIds.add(targetDocumentId);
      const targetDeviceId = toolCallsById.get(toolCallId)?.deviceId;
      if (targetDeviceId) {
        targetDeviceIds.add(targetDeviceId);
      }
    });

    if (newlyProcessedToolCallIds.size === 0) {
      return;
    }

    const processedToolCallIds = Array.from(newlyProcessedToolCallIds);
    newlyProcessedToolCallIds.forEach((toolCallId) => {
      processedToolCallIdsRef.current.add(toolCallId);
    });

    void (async () => {
      try {
        await Promise.all([
          ...(organizationChanged
            ? [queryClient.invalidateQueries({ queryKey: getUserQueryKey() })]
            : []),
          ...Array.from(systemDeviceIds, (deviceId) =>
            queryClient.invalidateQueries({
              queryKey: getDeviceQueryKey(deviceId),
            })
          ),
          ...(systemDeviceIds.size
            ? [
                queryClient.invalidateQueries({
                  queryKey: getDevicesQueryKey(orgId),
                }),
              ]
            : []),
        ]);
        await invalidateAnswerQueries({
          queryClient,
          orgId,
          deviceIds: Array.from(targetDeviceIds),
          documentIds: Array.from(targetDocumentIds),
        });
      } catch (error) {
        captureSentryException(error, {
          orgId,
          deviceIds: Array.from(targetDeviceIds),
          documentIds: Array.from(targetDocumentIds),
          toolCallIds: processedToolCallIds,
        });
      }
    })();
  }, [messages, orgId, queryClient]);
};
